/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Manage Customer Studio schema with Git", description = "Store and review Customer Studio schema changes in Git and keep the same schema configuration across environments.", lastUpdatedDate = new Date(1787788800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Manage Customer Studio schema with Git",
        description: "Store and review Customer Studio schema changes in Git and keep the same schema configuration across environments.",
        lastUpdatedDate: "Thu Aug 27 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Decide whether you need Schema Git Sync",
          "depth": 0,
          "id": "decide-whether-you-need-schema-git-sync"
        }, {
          "value": "Understand what Git Sync manages",
          "depth": 0,
          "id": "understand-what-git-sync-manages"
        }, {
          "value": "Choose how environments share schema configuration",
          "depth": 0,
          "id": "choose-how-environments-share-schema-configuration"
        }, {
          "value": "Use the same schema when staging and production should match",
          "depth": 1,
          "id": "use-the-same-schema-when-staging-and-production-should-match"
        }, {
          "value": "Use separate configuration when the schemas are different",
          "depth": 1,
          "id": "use-separate-configuration-when-the-schemas-are-different"
        }, {
          "value": "Connect each workspace to Git",
          "depth": 0,
          "id": "connect-each-workspace-to-git"
        }, {
          "value": "Map environment-specific resources",
          "depth": 0,
          "id": "map-environment-specific-resources"
        }, {
          "value": "Sync your existing schema",
          "depth": 0,
          "id": "sync-your-existing-schema"
        }, {
          "value": "Review and apply schema changes",
          "depth": 0,
          "id": "review-and-apply-schema-changes"
        }, {
          "value": "Changes to validate before production",
          "depth": 1,
          "id": "changes-to-validate-before-production"
        }, {
          "value": "Configure settings that Git Sync doesn't store",
          "depth": 0,
          "id": "configure-settings-that-git-sync-doesnt-store"
        }, {
          "value": "Validate the schema in each environment",
          "depth": 0,
          "id": "validate-the-schema-in-each-environment"
        }, {
          "value": "Troubleshoot resource mapping errors",
          "depth": 0,
          "id": "troubleshoot-resource-mapping-errors"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      img: "img",
      ol: "ol",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data engineering, analytics engineering, platform teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A working ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "Customer Studio schema"
                  })]
                }), _jsx("li", {
                  children: "A Git repository"
                }), _jsxs("li", {
                  children: [_jsx(_components.a, {
                    href: "/extensions/git-sync",
                    children: "Git Sync"
                  }), " enabled"]
                }), _jsx("li", {
                  children: "Permission to configure Git Sync and Customer Studio schema"
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Schema Git Sync stores Customer Studio models and relationships as YAML files in Git. Use it when your team wants to review schema changes through Git or keep the same schema configuration across environments such as staging and production."
        })
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Schema Git Sync"
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "decide-whether-you-need-schema-git-sync",
        children: "Decide whether you need Schema Git Sync"
      }), "\n", _jsx(_components.p, {
        children: "You don't need Git Sync to use Customer Studio. You can create and manage your entire schema directly in Hightouch."
      }), "\n", _jsx(_components.p, {
        children: "Use Schema Git Sync when your team needs one or more of these workflows:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Review schema changes through pull requests."
        }), "\n", _jsx(_components.li, {
          children: "Test schema changes in staging before applying them to production."
        }), "\n", _jsx(_components.li, {
          children: "Keep the same schema structure across multiple Hightouch workspaces."
        }), "\n", _jsx(_components.li, {
          children: "Keep a versioned history of schema configuration in Git."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Set it up after you have a working schema in Customer Studio. This makes it easier to understand the files Git Sync creates and to confirm the same schema produces the expected models, relationships, and audience results in each environment."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Schema Git Sync stores models and relationships in Git. Some Customer Studio\nsettings remain workspace-specific, including column privacy settings, display\nconfiguration, column suggestions, merge-column selections, and column\naliases."
        })
      }), "\n", _jsx(_components.h2, {
        id: "understand-what-git-sync-manages",
        children: "Understand what Git Sync manages"
      }), "\n", _jsx(_components.p, {
        children: "Schema Git Sync stores these Customer Studio resources in Git:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "parent models"
        }), "\n", _jsx(_components.li, {
          children: "related models"
        }), "\n", _jsx(_components.li, {
          children: "event models"
        }), "\n", _jsx(_components.li, {
          children: "relationships between models"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "When you make a supported change in Hightouch, Git Sync writes the updated configuration to YAML in your repository. Hightouch can also apply supported changes from Git to the workspace."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, a schema containing ", _jsx(_components.code, {
          children: "Users"
        }), " as a parent model, ", _jsx(_components.code, {
          children: "Purchases"
        }), " as a related model, ", _jsx(_components.code, {
          children: "Product Viewed"
        }), " as an event model, and the relationships connecting them is represented as separate YAML files in Git."]
      }), "\n", _jsxs(_components.p, {
        children: ["You don't need to create these files yourself. Create and configure the schema in Hightouch first, then use the generated YAML to review or edit configuration in Git. For YAML fields and supported configuration, see ", _jsx(_components.a, {
          href: "/extensions/git-sync#customer-studio-schema",
          children: "Customer Studio schema in Git Sync"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Field names in the YAML are case-sensitive. If a column's casing changes in\nthe warehouse (for example, ", _jsx(_components.code, {
            children: "user_id"
          }), " to ", _jsx(_components.code, {
            children: "User_Id"
          }), "), Git Sync treats it as a\ndifferent field. Match the exact casing your warehouse uses."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "choose-how-environments-share-schema-configuration",
        children: "Choose how environments share schema configuration"
      }), "\n", _jsx(_components.p, {
        children: "If you use separate Hightouch workspaces for staging and production, decide whether they should share the same schema."
      }), "\n", _jsx(_components.h3, {
        id: "use-the-same-schema-when-staging-and-production-should-match",
        children: "Use the same schema when staging and production should match"
      }), "\n", _jsx(_components.p, {
        children: "For most teams, staging and production should use the same parent models, related models, events, and relationships."
      }), "\n", _jsx(_components.p, {
        children: "Point both workspaces at the same schema configuration when:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "they represent the same Customer Studio setup"
        }), "\n", _jsx(_components.li, {
          children: "differences are limited to environment-specific sources or destinations"
        }), "\n", _jsx(_components.li, {
          children: "you want to test a schema change before promoting it to production"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The warehouse connections may differ between the two workspaces, but the Customer Studio schema stays the same. Git Sync maps the environment-specific sources and destinations with ", _jsx(_components.a, {
          href: "#map-environment-specific-resources",
          children: "alias files"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "use-separate-configuration-when-the-schemas-are-different",
        children: "Use separate configuration when the schemas are different"
      }), "\n", _jsx(_components.p, {
        children: "Use separate branches or paths when the schemas themselves should be different — for example, when your staging workspace contains an experimental parent model or relationship that you don't want production to read yet."
      }), "\n", _jsx(_components.p, {
        children: "Don't create separate schema configuration just because staging and production point to different warehouse resources. Git Sync can map environment-specific sources and destinations without duplicating the schema."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "connect-each-workspace-to-git",
        children: "Connect each workspace to Git"
      }), "\n", _jsxs(_components.p, {
        children: ["Configure Git Sync from ", _jsx(_components.strong, {
          children: "Integrations > Extensions > Version control with Git"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/schema/git-sync-extension.png",
          alt: "The Extensions page in Hightouch with Version control with Git highlighted"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you haven't connected Git yet, follow the ", _jsx(_components.a, {
          href: "/extensions/git-sync#setup",
          children: "Git Sync setup guide"
        }), " to:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Authenticate to your Git provider."
        }), "\n", _jsx(_components.li, {
          children: "Choose the repository."
        }), "\n", _jsx(_components.li, {
          children: "Configure the branch and path the workspace should use."
        }), "\n", _jsx(_components.li, {
          children: "Enable Git Sync."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You authenticate by choosing a Git service and connecting its app:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/schema/git-sync-credentials.png",
          alt: "Git credentials dialog showing a connected GitHub App"
        })
      }), "\n", _jsx(_components.p, {
        children: "Then select the repository, branch, and optional path Hightouch should use:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/schema/git-sync-repository-branch-path.png",
          alt: "Repository, branch, and optional path fields for a Git Sync connection"
        })
      }), "\n", _jsx(_components.p, {
        children: "Repeat this for each Hightouch workspace that should share schema configuration. Once connected, Hightouch creates the required Git Sync files and starts tracking supported resources."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Branches don't need to exist before setup. Hightouch can create them when you\nconfigure Git Sync."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "map-environment-specific-resources",
        children: "Map environment-specific resources"
      }), "\n", _jsxs(_components.p, {
        children: ["Staging and production often use different warehouse sources or destinations — for example, a ", _jsx(_components.code, {
          children: "snowflake-dev"
        }), " source in staging and ", _jsx(_components.code, {
          children: "snowflake-prod"
        }), " in production. The Customer Studio schema can still stay identical."]
      }), "\n", _jsxs(_components.p, {
        children: ["Git Sync uses ", _jsx(_components.strong, {
          children: "alias files"
        }), " to map each shared source or destination reference to the corresponding resource in each workspace. Hightouch creates one alias file per connected workspace, named for that workspace (for example, ", _jsx(_components.code, {
          children: "aliases-staging.yaml"
        }), "):"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# aliases-staging.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "main-database:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "snowflake-dev"
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# aliases-production.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "main-database:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "snowflake-prod"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Both environments use ", _jsx(_components.code, {
          children: "main-database"
        }), " in the shared schema configuration, and each alias maps it to the warehouse source for that workspace. Hightouch creates the alias file automatically. Edit the mappings only when equivalent resources have different identifiers between environments."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Source and destination identifiers can differ between environments when\naliases map them correctly. Model identifiers must stay consistent when\nworkspaces share the same schema files."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For a full multi-environment walkthrough, see ", _jsx(_components.a, {
          href: "/extensions/git-sync#connect-staging-and-production-workspaces",
          children: "Connect staging and production workspaces"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "sync-your-existing-schema",
        children: "Sync your existing schema"
      }), "\n", _jsx(_components.p, {
        children: "When Schema Git Sync is enabled, Hightouch performs an initial sync of the supported schema configuration to Git. The generated repository includes files for your models and relationships under the schema directory."
      }), "\n", _jsxs(_components.p, {
        children: ["After the initial sync, schema changes made in Customer Studio are written to Git automatically. You can monitor Git Sync from the ", _jsx(_components.strong, {
          children: "Runs"
        }), " tab or inspect the resulting commits in your repository."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "You don't need to manage the order in which models and relationships are\nwritten. Hightouch saves models before relationships so that referenced models\nexist before their relationships are created."
        })
      }), "\n", _jsx(_components.p, {
        children: "For large schemas, the initial sync can take longer than later incremental updates."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "review-and-apply-schema-changes",
        children: "Review and apply schema changes"
      }), "\n", _jsx(_components.p, {
        children: "A schema change can affect existing audiences, traits, syncs, and journeys. Review which audiences, traits, syncs, and journeys the change could affect before you apply it to production."
      }), "\n", _jsx(_components.p, {
        children: "A common workflow is:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Make the schema change in staging."
        }), "\n", _jsx(_components.li, {
          children: "Build an audience that uses the changed model, field, or relationship."
        }), "\n", _jsx(_components.li, {
          children: "Confirm the audience count and sample records match the expected results."
        }), "\n", _jsx(_components.li, {
          children: "Review the generated Git changes."
        }), "\n", _jsx(_components.li, {
          children: "Merge the change through your normal Git workflow."
        }), "\n", _jsx(_components.li, {
          children: "Validate the same audience in production."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Enable Hightouch ", _jsx(_components.a, {
          href: "/extensions/git-sync#validate-changes",
          children: "CI checks"
        }), " on the repository to catch breaking changes on a pull request — for example, deleting a model that a sync depends on — before the change merges."]
      }), "\n", _jsx(_components.h3, {
        id: "changes-to-validate-before-production",
        children: "Changes to validate before production"
      }), "\n", _jsx(_components.p, {
        children: "Revalidate dependent audiences, traits, syncs, and journeys when a change alters how existing data is identified or connected:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "changing what one row in a parent model represents"
        }), "\n", _jsx(_components.li, {
          children: "changing a primary key"
        }), "\n", _jsx(_components.li, {
          children: "removing or renaming a field used by audiences, traits, syncs, or journeys"
        }), "\n", _jsx(_components.li, {
          children: "changing the fields used to connect two models"
        }), "\n", _jsx(_components.li, {
          children: "changing whether a relationship is one-to-one or one-to-many"
        }), "\n", _jsx(_components.li, {
          children: "deleting and recreating a relationship"
        }), "\n", _jsx(_components.li, {
          children: "renaming a model or changing its identifier"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Add replacement fields or relationships before removing existing ones when dependent assets still use them. For example, add a replacement field, update dependent audiences and traits to use it, then remove the old configuration after you confirm nothing still depends on it. Recreating a relationship generates a new internal ID, so review the model's ", _jsx(_components.strong, {
          children: "Activity"
        }), " tab and rebuild a few affected audiences after relationship changes."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "configure-settings-that-git-sync-doesnt-store",
        children: "Configure settings that Git Sync doesn't store"
      }), "\n", _jsx(_components.p, {
        children: "Not every Customer Studio setting is stored in Git. After you apply structural schema changes to another workspace, review any workspace-specific settings affected by the change."
      }), "\n", _jsx(_components.p, {
        children: "Depending on the change, you may need to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "refresh available warehouse columns"
        }), "\n", _jsx(_components.li, {
          children: "configure column privacy"
        }), "\n", _jsx(_components.li, {
          children: "restore display names or descriptions"
        }), "\n", _jsx(_components.li, {
          children: "configure filter suggestions"
        }), "\n", _jsx(_components.li, {
          children: "set merge columns"
        }), "\n", _jsx(_components.li, {
          children: "update traits or audiences that depend on changed fields"
        }), "\n", _jsx(_components.li, {
          children: "validate any affected relationship paths"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A successful Git Sync run confirms that Hightouch applied the stored schema configuration. It doesn't confirm that audiences, relationships, privacy settings, or field visibility work as expected."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "validate-the-schema-in-each-environment",
        children: "Validate the schema in each environment"
      }), "\n", _jsx(_components.p, {
        children: "After your initial setup, or after promoting an important schema change, test the result in Customer Studio. At minimum:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Confirm the expected parent, related, and event models appear."
        }), "\n", _jsx(_components.li, {
          children: "Confirm relationships connect the correct models and fields."
        }), "\n", _jsx(_components.li, {
          children: "Build an audience that uses the changed part of the schema."
        }), "\n", _jsx(_components.li, {
          children: "Compare the result with a warehouse query or the validated result from another environment."
        }), "\n", _jsx(_components.li, {
          children: "Confirm sensitive fields have the intended privacy settings."
        }), "\n", _jsx(_components.li, {
          children: "Test through relationships end to end if you use them."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For multi-environment setups, also confirm that each workspace maps to the intended warehouse source and destination."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "troubleshoot-resource-mapping-errors",
        children: "Troubleshoot resource mapping errors"
      }), "\n", _jsx(_components.p, {
        children: "If Git Sync can't match a schema resource in Git to a resource in the Hightouch workspace, a run can fail with an error such as:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "error serializing resource\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "This usually means Git Sync can't match one of the resources referenced by the configuration. Check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Model identifiers"
          }), " — Shared models use the same identifiers in each workspace."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Aliases"
          }), " — Source and destination aliases point to resources that exist in the current workspace."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Branch or path"
          }), " — The workspace is reading the intended Git configuration."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Environment setup"
          }), " — Required sources and destinations exist before the schema references them."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["After correcting the configuration, trigger a full resync. For additional errors, see the ", _jsx(_components.a, {
          href: "/extensions/git-sync#faq",
          children: "Git Sync troubleshooting guide"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "Define your schema"
          }), " to create or update Customer Studio models and relationships."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/extensions/git-sync",
            children: "Git Sync"
          }), " to configure authentication, repository settings, and other version-controlled Hightouch resources."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/extensions/git-sync#connect-staging-and-production-workspaces",
            children: "Connect staging and production workspaces"
          }), " for a multi-environment setup."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
